import Button from '@atoms/Button/Button';
import GridSlider, { GridSize } from '@atoms/GridSlider/GridSlider';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import Search from '@atoms/Search/Search';
import Tag from '@atoms/Tag/Tag';
import Text from '@atoms/Text/Text';
import ListingTypeEnum from '@models/enums/ListingTypeEnum';
import SizeEnum from '@models/enums/SizeEnum';
import { IColourSwatchCard } from '@models/IColourSwatchCard';
import { IHotspotCard } from '@models/IHotspotCard';
import { IInspirationCard } from '@models/IInspirationCard';
import {
  cardTypes,
  IListing,
  IListingFilterGroup,
  requestState,
} from '@models/IListing';
import { IProductCard } from '@models/IProductCard';
import ColourSwatchCard, {
  ColourSwatchCardBG,
} from '@molecules/ColourSwatchCard/ColourSwatchCard';
import DrawerWrapper from '@molecules/DrawerWrapper/DrawerWrapper';
import FilterAccordion from '@molecules/FilterAccordion/FilterAccordion';
import HotspotCard from '@molecules/HotspotCard/HotspotCard';
import InspirationCard from '@molecules/InspirationCard/InspirationCard';
import ListingColourBar from '@molecules/ListingColourBar/ListingColourBar';
import ListingFilters from '@molecules/ListingFilters/ListingFilters';
import ListingPlaceholder from '@molecules/ListingPlaceholder/ListingPlaceholder';
import ListingProductBar from '@molecules/ListingProductBar/ListingProductBar';
import ProductCard from '@molecules/ProductCard/ProductCard';
import { fadeIn } from '@utility/config/animations';
import replaceValuesInLabel from '@utility/helpers/replaceValuesInLabel';
import useBreakpoint from '@utility/hooks/useBreakpoint';
import useListingComponentData from '@utility/hooks/useListingComponentData';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import queryString from 'query-string';
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { getGridConfig, sanitizeQuery } from './grid';

type QueryState = {
  readonly freetext?: string;
  readonly page: number;
  readonly pageUrl: string;
  readonly take: number;
  readonly [key: string]: any; // Custom query params
};

type SelectedFilter = {
  readonly featuredInspiration?: IHotspotCard;
  readonly inspirationCard?: IInspirationCard;
  readonly id: string;
  readonly label: string;
  readonly name: string;
};

interface Props extends IListing {
  readonly additionalFilters?: string[];
  readonly customQueryParams?: any;
  readonly initialFilters?: any;
  readonly listingFilterParam: string;
  readonly type: ListingTypeEnum;
}

const ListingWrapper = ({
  additionalFilters = [],
  applicationAreas,
  applyLabel,
  autoCompleteEndpoint,
  autoCompletePlaceholder,
  clearAllLabel,
  closeLabel,
  colourBar,
  countLabel,
  customQueryParams = {},
  featuredInspiration,
  filterLabel,
  filters,
  initialFilters,
  inspirationCard,
  listingFilterParam,
  listingFilters,
  loadMoreLabel,
  loadingLabel,
  noResultsHeader,
  noResultsLabel,
  noResultsText,
  openLabel,
  pageUrl,
  productBar,
  resultsLabel,
  searchEndpoint,
  subtitle,
  title,
  type,
}: Props) => {
  const breakpoint = useBreakpoint();
  const [gridSize, setGridSize] = useState<GridSize>(
    type === ListingTypeEnum.Colour ? 50 : 100,
  );
  const [listItems, setListItems] = useState<cardTypes | []>([]);
  const [slicedListitems, setSlicedListItems] = useState<cardTypes | []>([]);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([]);
  const [sortedListingFilters, setSortedListingFilters] = useState<
    IListingFilterGroup[]
  >([]);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [selectedFeaturedInspiration, setSelectedFeaturedInspiration] =
    useState<IHotspotCard | undefined>(featuredInspiration);
  const [selectedInspirationCard, setSelectedInspirationCard] = useState<
    IInspirationCard | undefined
  >(inspirationCard);
  const [clearSearch, setClearSearch] = useState(0);
  const resultsRef = useRef<HTMLDivElement>(null);
  const hasPopulatedQueryState = useRef(false);

  type CardData = {
    readonly show: boolean;
    readonly occupiedSpaceCount: number;
    readonly aspectRatio?: number;
  };

  const [hotspotData, setHotspotData] = useState<CardData>({
    show: false,
    occupiedSpaceCount: 0,
    aspectRatio: undefined,
  });
  const [inspirationData, setInspirationData] = useState<CardData>({
    show: false,
    occupiedSpaceCount: 0,
  });

  const params =
    typeof window !== 'undefined'
      ? queryString.pick(window?.location.search, [
          'freetext',
          listingFilterParam,
          ...filters.map((filter) => String(filter.id)),
          ...additionalFilters.map((filter) => filter),
        ])
      : '';

  const parsedParams = queryString.parse(params, {
    arrayFormat: 'comma',
  });

  const parsedUrlParams = Object.entries(parsedParams).reduce(
    (acc: Record<string, any>, [key, value]) => {
      if (key === listingFilterParam) {
        if (Array.isArray(value)) {
          acc[key] = value;
        } else {
          acc[key] = value ? [value] : [];
        }
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {},
  );

  const hasParams = Object.keys(parsedUrlParams).length > 0;

  const existingFilters = hasParams ? parsedUrlParams : initialFilters;
  const initialSearchText = parsedUrlParams ? parsedUrlParams?.freetext : '';

  // Adjust cardVariant, gridClasses and cardSize based on gridSize
  const {
    gridClasses,
    cardSize,
    cardVariant,
    hotspotPosition,
    colourInspirationPosition,
    productInspirationPosition,
    hotspot,
    inspiration,
  } = getGridConfig(gridSize);

  // Work out whether to show featured inspiration and inspiration card based on breakpoint and amount of items shown
  type Breakpoint = 'sm' | 'md' | 'xl';

  const mapBreakpoint = (breakpoint: Breakpoint): Breakpoint => {
    const mapping: Record<string, Breakpoint> = {
      sm: 'sm',
      md: 'md',
      lg: 'md', // 'lg' mapped to 'md'
      xl: 'xl',
      xxl: 'xl', // 'xxl' mapped to 'xl'
    };
    return mapping[breakpoint] || breakpoint;
  };

  const positionClass = (
    cardType: 'product' | 'colour' | 'inspiration' | 'hotspot',
  ) => {
    switch (cardType) {
      case 'hotspot':
        return hotspotPosition;
      case 'inspiration':
        return type === ListingTypeEnum.Product
          ? productInspirationPosition
          : colourInspirationPosition;
      default:
        return cardSize;
    }
  };

  useEffect(() => {
    if (selectedFeaturedInspiration || selectedInspirationCard) {
      const normalizedBreakpoint = mapBreakpoint(breakpoint as Breakpoint);

      setHotspotData({
        show:
          (selectedFeaturedInspiration &&
            listItems.length >=
              hotspot.minResultsToShow[normalizedBreakpoint]) ||
          false,
        occupiedSpaceCount:
          hotspot.occupiedSpaceCount[normalizedBreakpoint] || 0,
        aspectRatio: hotspot.aspectRatio[normalizedBreakpoint],
      });

      setInspirationData({
        show:
          (selectedInspirationCard &&
            listItems.length >=
              inspiration.minResultsToShow[normalizedBreakpoint]) ||
          false,
        occupiedSpaceCount:
          inspiration.occupiedSpaceCount[normalizedBreakpoint] || 0,
      });
    }
  }, [
    selectedFeaturedInspiration,
    selectedInspirationCard,
    breakpoint,
    listItems,
  ]);

  const calculatedPageSize = useMemo(() => {
    const pageSizes = {
      100: breakpoint === 'sm' ? 10 : 20,
      50: breakpoint === 'sm' ? 18 : 36,
      0: breakpoint === 'sm' ? 40 : 144,
    };

    return pageSizes[gridSize];
  }, [gridSize, breakpoint]);

  // Remove hotspot and inspiration occupied space from listItems
  useEffect(() => {
    const count =
      listItems.length -
      (hotspotData.show ? hotspotData.occupiedSpaceCount : 0) -
      (inspirationData.show ? inspirationData.occupiedSpaceCount : 0);

    const newSlicedListItems =
      data.total <= listItems.length || data.count < calculatedPageSize
        ? listItems
        : listItems.slice(0, count);

    setSlicedListItems(newSlicedListItems);
  }, [listItems, hotspotData, inspirationData]);

  const defaultState = {
    applicationAreas,
    freetext: '',
    page: 0,
    pageUrl,
    take: calculatedPageSize,
  };

  const [queryState, setQueryState] = useState<QueryState>({
    ...defaultState,
    ...customQueryParams,
  });

  const { pageUrl: formPageUrl, freetext, page, take, ...rest } = queryState;

  // Remove any customQueryParams to get the drawerFilters
  const drawerFilters = Object.fromEntries(
    Object.entries(rest).filter(
      ([key]) => !Object.keys(customQueryParams).includes(key),
    ),
  );

  const mappedDrawerFilters: SelectedFilter[] = Object.entries(
    drawerFilters,
  ).map(([key, value]) => ({
    id: value as string,
    name: key,
    label: `Filter for ${key}`,
  }));

  const setCleanQueryParams = (data: QueryState) => {
    const cleanQuery = sanitizeQuery<QueryState>({
      ...customQueryParams,
      ...data,
      pageUrl,
    });

    // Remove specific parameters
    const {
      applicationAreas,
      page,
      take,
      pageUrl: filteredUrl,
      ...filteredQuery
    } = cleanQuery;

    const queryStringified = queryString.stringify(filteredQuery, {
      arrayFormat: 'comma',
    });

    setQueryState(cleanQuery);

    const newUrl = queryStringified
      ? `${window.location.pathname}?${queryStringified}`
      : window.location.pathname;

    window.history.replaceState(null, '', newUrl);
  };

  // Populate queryState using url query params on client side
  useEffect(() => {
    setValue('freetext', parsedUrlParams.freetext);

    setCleanQueryParams({
      ...parsedUrlParams,
      ...drawerFilters,
      ...customQueryParams,
      ...existingFilters,
      page: 0,
    });

    setSelectedFilters(mappedDrawerFilters);
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({ defaultValues: queryState });

  const filterIds = filters.map((filter) => filter.id);
  const checkedDrawerFilters = watch(filterIds);
  const mappedCheckedDrawerFilters = checkedDrawerFilters
    .flatMap((value) => value)
    .filter(Boolean);

  const {
    fetchData,
    state: { data, loading, error },
  } = useListingComponentData<
    { readonly [key: string]: unknown },
    requestState
  >(searchEndpoint, { results: listItems, total: 0, count: 0 }, false, false);

  const handleListingFiltersUpdate = (
    selectedListingFilters: IListingFilterGroup[],
  ) => {
    const listingFilters = selectedListingFilters.map(({ filter }) =>
      filter.id.toString(),
    );

    setSortedListingFilters(selectedListingFilters);

    setCleanQueryParams({
      ...queryState,
      [listingFilterParam]: listingFilters,
      page: 0,
    });
  };

  // Function to clear all filters and query params
  const clearAllFilters = (
    e: MouseEvent<HTMLButtonElement>,
    params: Partial<typeof queryState> = {},
  ) => {
    e.preventDefault();

    if (checkedDrawerFilters) reset(checkedDrawerFilters);

    // Resets all the current selected filters
    reset(filterIds);
    reset({ freetext: '' });

    setClearSearch((prev) => prev + 1);
    setSortedListingFilters([]);
    setCleanQueryParams({
      freetext: '',
      take: queryState.take,
      page: 0,
      applicationAreas,
      ...params,
      ...customQueryParams,
    });

    setIsMobileFiltersOpen(false);
  };

  // Remove filter from selectedFilters when Tag is clicked
  const handleTagRemove = (id: string, key: string) => {
    const updatedFilters = selectedFilters.filter((filter) => filter.id !== id);
    setSelectedFilters(updatedFilters);

    const params = () => {
      const currentValue = ([] as any[]).concat(
        queryState[key as keyof typeof queryState] || [],
      );

      if (key === 'freetext') {
        return { freetext: '' };
      }

      return {
        [key]: currentValue.filter((item) => item !== id),
      };
    };

    if (key === 'freetext') setClearSearch((prev) => prev + 1);

    setCleanQueryParams({
      ...queryState,
      ...params(),
    });
  };

  // Form submission handler
  const onSubmit: SubmitHandler<QueryState> = async (data, e) => {
    e?.preventDefault();

    const { applicationAreas, page, pageUrl, freetext, ...selectedFilters } =
      data;

    // get additional filters from data based on the additionalFilters keys passed from parent
    const additionalFiltersData = additionalFilters.reduce(
      (acc: Record<string, string[]>, key) => {
        if (data[key] !== undefined) {
          acc[key] = data[key];
        }
        return acc;
      },
      {},
    );

    // Reset filters when a new search term is submitted
    if (!!freetext && freetext !== queryState.freetext) {
      setCleanQueryParams({
        freetext,
        page: 0,
        take: queryState.take,
        applicationAreas,
        ...customQueryParams,
        ...additionalFiltersData,
      });
    } else {
      setCleanQueryParams({
        ...queryState,
        ...data,
        page: 0,
      });
    }

    window.dataLayer.push({
      event: 'trackingEvent_filter_select',
      trackingEvent_filter_select: { ...selectedFilters },
    });

    // Scroll to listing results
    const headerHeight = document.getElementById('header')?.offsetHeight || 0;

    if (resultsRef.current) {
      const resultsOffset =
        resultsRef.current.getBoundingClientRect().top +
        window.scrollY -
        headerHeight;

      window.scrollTo({
        top: resultsOffset,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const updatedPageSize = calculatedPageSize;

    const cleanQuery = sanitizeQuery<QueryState>(queryState);

    // Fetch data with the sanitized query parameters
    if (hasPopulatedQueryState.current) {
      fetchData({
        ...cleanQuery,
        take: updatedPageSize,
      });
    }

    // Reset with the current queryState
    reset(queryState);

    const { freetext } = queryState;
    const updatedSelectedFreetext = freetext
      ? [{ name: 'freetext', id: 'freetext', label: freetext }]
      : [];

    // Flatten and transform the filters based on selected filter values
    const updatedSelectedFilters = filters?.flatMap((filter) => {
      const mappedFilter = mappedDrawerFilters.find(
        (mapped) => mapped.name === filter.id,
      );

      if (!mappedFilter) return [];

      // Map the options where the id of the option matches one of the ids in the mappedFilter.id array
      return filter.options
        .filter((option) => mappedFilter.id.includes(option.id))
        .map(({ id, label }) => ({
          name: filter.id,
          id,
          label,
        }));
    });

    // Match listing filters with query state
    const findMatchingItems = (
      items: any[],
      idAccessor: { (item: any): any; (arg0: any): any },
    ) => {
      // Extract all arrays from queryState
      const queryArrays = Object.values(queryState).filter((value) =>
        Array.isArray(value),
      );

      const queryIds = queryArrays.flat().map(String);

      // Filter items by matching IDs
      return items.filter((item) =>
        queryIds.includes(String(idAccessor(item))),
      );
    };

    const selectedListingFilters = listingFilters
      ? findMatchingItems(listingFilters, (item) => item.filter.id).reduce(
          (acc, item) => {
            acc.push({
              name: listingFilterParam,
              id: item.filter.id.toString(),
              label: item.filter.label,
              featuredInspiration: item.featuredInspiration || null,
              inspirationCard: item.inspirationCard || null,
            });
            return acc;
          },
          [],
        )
      : undefined;

    // Set all selected filters
    setSelectedFilters([
      ...(updatedSelectedFilters || []),
      ...updatedSelectedFreetext,
      ...(selectedListingFilters || []),
    ]);
  }, [queryState]);

  // Update results
  useEffect(() => {
    hasPopulatedQueryState.current = true;

    if (queryState.page === 0) {
      setListItems(data.results ?? []);
    } else {
      setListItems((prevItems) => [
        ...(prevItems ?? []),
        ...(data.results ?? []),
      ]);
    }

    if (data?.results) {
      window.dataLayer.push({
        event: 'trackingEvent_search_results',
        event_name_variable: 'search_results',
        trackingEvent_search_results: {
          search_type: type,
          search_term: queryState.freetext,
          search_results: data.results.length > 0,
        },
      });
    }

    setSelectedFeaturedInspiration(
      sortedListingFilters[0]?.featuredInspiration || featuredInspiration,
    );
    setSelectedInspirationCard(
      sortedListingFilters[0]?.inspirationCard || inspirationCard,
    );
  }, [data]);

  // Fetch new results if grid size changes
  useEffect(() => {
    setQueryState((prev) => ({
      ...prev,
      page: 0,
    }));
  }, [gridSize]);

  useEffect(() => {
    setShowLoadMore(data?.total > slicedListitems.length);
  }, [slicedListitems]);

  const fadeInUpAnimation = {
    hidden: {
      opacity: 0,
      y: 50,
      transition: {
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1],
      },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 50,
        damping: 14,
      },
    },
  };

  return (
    <section className="tw-relative">
      <div className="tw-flex tw-flex-col">
        <div className="tw-bg-white tw-pb-xl md:tw-pb-2xl xl:tw-pb-xl">
          <div className="tw-container tw-grid tw-grid-cols-4 tw-gap-x-xs tw-gap-y-md tw-pt-xl tw-text-black md:tw-grid-cols-6 md:tw-gap-x-md md:tw-gap-y-xl lg:tw-grid-cols-12 xl:tw-pt-2xl">
            {title && (
              <div
                className="tw-col-span-full tw-flex tw-flex-col tw-items-center tw-gap-y-md tw-text-center md:tw-gap-y-lg xl:tw-col-span-6
xl:tw-col-start-4"
              >
                <Text variant="heading-56" className="tw-hidden md:tw-block">
                  {title}
                </Text>
                <Text variant="heading-32" className="tw-block md:tw-hidden">
                  {title}
                </Text>
                {subtitle && (
                  <Text
                    variant="body"
                    className="tw-rich-text tw-text-grey-80 md:tw-mb-0"
                    dangerouslySetInnerHTML={{ __html: subtitle }}
                  />
                )}
              </div>
            )}
            <div className="tw-col-span-full tw-col-start-1 tw-flex-col tw-gap-xs tw-pb-md md:tw-col-span-4 md:tw-col-start-2 md:tw-flex md:tw-pb-2xl lg:tw-col-start-5">
              <form onSubmit={handleSubmit(onSubmit)}>
                <Search
                  clearSearch={clearSearch}
                  autoCompleteEndpoint={`${autoCompleteEndpoint}?page=0&pageUrl=${pageUrl}&applicationAreas=${applicationAreas}&freetext`}
                  setValue={setValue as any}
                  errors={errors}
                  label={'Search'}
                  name={'freetext'}
                  initialValue={initialSearchText ?? ''}
                  register={register as any}
                  placeholder={autoCompletePlaceholder}
                  onAutoCompleteSelect={(value) => {
                    onSubmit({
                      ...queryState,
                      freetext: value,
                    });
                  }}
                />
              </form>
            </div>
          </div>
          {listingFilters && (
            <ListingFilters
              type={type}
              filters={listingFilters}
              selectedListingFilters={queryState[listingFilterParam]}
              onClickCallback={(selectedListingFilters) => {
                handleListingFiltersUpdate(selectedListingFilters);
              }}
            />
          )}
        </div>

        <div
          ref={resultsRef}
          className="dark tw-col-span-full tw-min-h-[700px]"
        >
          {/* Filters */}
          <div className="lg:pb-md tw-sticky tw-top-[56px] tw-z-30 tw-bg-grey-10 tw-py-xs lg:tw-hidden">
            {/* Mobile Filters */}
            {!!filters.length && (
              <div className="tw-container tw-block lg:tw-inline-block">
                <Button
                  aria-label={openLabel}
                  aria-controls="filters-modal"
                  aria-expanded={isMobileFiltersOpen}
                  variant="white"
                  label={openLabel}
                  iconName="filter"
                  buttonClasses="tw-w-full [&>div]:tw-justify-between tw-border tw-border-grey-10 hover:tw-border-black"
                  onClick={() => setIsMobileFiltersOpen(true)}
                />
              </div>
            )}
          </div>

          <div className="tw-container tw-flex tw-flex-col tw-items-end tw-justify-between tw-gap-sm tw-pb-xs md:tw-py-sm lg:tw-flex-row lg:tw-pb-md lg:tw-pt-lg">
            <AnimatePresence mode="wait">
              {!!filters.length && (
                <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-gap-xs md:tw-justify-end lg:tw-items-start">
                  <Button
                    aria-label={openLabel}
                    aria-controls="filters-modal"
                    aria-expanded={isMobileFiltersOpen}
                    variant="white"
                    label={openLabel}
                    iconName="filter"
                    buttonClasses="tw-w-full lg:tw-w-auto [&>div]:tw-justify-between tw-border tw-border-grey-10 hover:tw-border-black tw-hidden lg:tw-flex"
                    onClick={() => setIsMobileFiltersOpen(true)}
                  />
                  {/* Quick Filters */}
                  {!!selectedFilters.length && (
                    <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-2 md:tw-gap-3">
                      {selectedFilters.map(({ name, label, id }, index) => (
                        <motion.div
                          key={`${id}-${index}`}
                          variants={fadeIn}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                        >
                          <Tag
                            label={label}
                            onClick={() => {
                              handleTagRemove(id, name);
                            }}
                            icon
                            largeText
                          />
                        </motion.div>
                      ))}

                      <motion.div
                        key="clear"
                        variants={fadeIn}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                      >
                        <Button
                          variant="link"
                          buttonClasses="tw-py-[2px] !tw-px-2"
                          label={clearAllLabel}
                          onClick={(e) => {
                            clearAllFilters(e, {});
                          }}
                        />
                      </motion.div>
                    </div>
                  )}
                </div>
              )}
            </AnimatePresence>

            <div className="tw-relative tw-flex tw-w-full tw-shrink-0 tw-flex-col tw-justify-between tw-gap-sm md:tw-flex-row md:tw-items-center lg:tw-w-auto lg:tw-flex-col lg:tw-items-end lg:tw-justify-end xl:tw-gap-xs">
              <div className="tw-h-5">
                <AnimatePresence mode="wait">
                  {hasPopulatedQueryState.current && !loading && (
                    <motion.div
                      key="results"
                      variants={fadeIn}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                    >
                      <Text
                        variant="body-small"
                        className="tw-text-left tw-text-grey-70"
                      >
                        {!loading && slicedListitems?.length === 0
                          ? noResultsLabel
                          : replaceValuesInLabel(countLabel, [
                              slicedListitems ? slicedListitems?.length : 0,
                              data?.total,
                            ])}
                      </Text>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {type === ListingTypeEnum.Colour && (
                <GridSlider value={gridSize} setValue={setGridSize} />
              )}
            </div>
          </div>
          {colourBar?.colourCard && (
            <div className="tw-container tw-mb-14 xl:tw-mb-[84px]">
              <ListingColourBar {...colourBar} />
            </div>
          )}
          {productBar?.productCard && (
            <div className="tw-container tw-mb-14 xl:tw-mb-[84px]">
              <ListingProductBar {...productBar} />
            </div>
          )}
          {slicedListitems && (
            <div
              className={classNames(
                'tw-transition-ease tw-container tw-relative tw-grid tw-grid-cols-12 tw-items-center',
                gridClasses,
              )}
            >
              <AnimatePresence mode="wait">
                {(!loading || page !== 0) && (
                  <>
                    {/* No results message */}
                    {!slicedListitems?.length &&
                      hasPopulatedQueryState.current && (
                        <motion.div
                          key="no-results"
                          variants={fadeInUpAnimation}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          className="tw-absolute tw-inset-0 tw-flex tw-h-[400px] tw-items-center"
                        >
                          <ListingPlaceholder
                            title={noResultsHeader}
                            message={noResultsText}
                          />
                        </motion.div>
                      )}
                    {/* Hotspot Card */}
                    {!!selectedFeaturedInspiration && hotspotData.show && (
                      <motion.div
                        key={`hotspot-${selectedFeaturedInspiration?.link}`}
                        variants={fadeInUpAnimation}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        className={classNames(
                          'tw-h-full',
                          positionClass('hotspot'),
                        )}
                      >
                        <HotspotCard
                          aspectRatio={hotspotData.aspectRatio}
                          {...selectedFeaturedInspiration}
                        />
                      </motion.div>
                    )}

                    {/* Inspiration Card */}
                    {!!selectedInspirationCard && inspirationData.show && (
                      <motion.div
                        key={`inspiration-${selectedInspirationCard.meta?.contentLink}`}
                        variants={fadeInUpAnimation}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        className={classNames(
                          'tw-h-full',
                          positionClass('inspiration'),
                        )}
                      >
                        <InspirationCard {...selectedInspirationCard} />
                      </motion.div>
                    )}

                    {/* Listing Cards */}
                    {!!slicedListitems?.length &&
                      slicedListitems.map((card, index) => {
                        if (type === ListingTypeEnum.Colour) {
                          const { colourName } = card as IColourSwatchCard;
                          return (
                            <motion.div
                              key={`${colourName}-${index}`}
                              variants={fadeInUpAnimation}
                              initial="hidden"
                              animate="visible"
                              exit="hidden"
                              className={classNames(
                                'tw-h-full',
                                positionClass('colour'),
                              )}
                            >
                              <ColourSwatchCard
                                createColourImage={false}
                                showLikeButton={gridSize !== 0}
                                bgColour={ColourSwatchCardBG.White}
                                {...(card as IColourSwatchCard)}
                                variant={cardVariant}
                              />
                            </motion.div>
                          );
                        }

                        if (type === ListingTypeEnum.Product) {
                          const { productName } = card as IProductCard;
                          return (
                            <motion.div
                              key={`${productName}-${index}`}
                              variants={fadeInUpAnimation}
                              initial="hidden"
                              animate="visible"
                              exit="hidden"
                              className={classNames(
                                'tw-h-full',
                                positionClass('product'),
                              )}
                            >
                              <ProductCard
                                {...(card as IProductCard)}
                                detailedView
                              />
                            </motion.div>
                          );
                        }

                        return null;
                      })}
                  </>
                )}
              </AnimatePresence>
            </div>
          )}
          {/* Load more button */}
          {!!slicedListitems.length && (
            <motion.div
              variants={fadeInUpAnimation}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="tw-flex tw-min-h-[112px] tw-items-center tw-justify-center"
            >
              <AnimatePresence mode="wait">
                {loading ? (
                  <div className="tw-col-span-full tw-flex tw-justify-center tw-py-6">
                    <LoadingSpinner
                      size={SizeEnum.Small}
                      loadingLabel={loadingLabel}
                    />
                  </div>
                ) : (
                  <motion.div
                    variants={fadeInUpAnimation}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className="tw-mt-lg tw-flex tw-flex-col tw-items-center tw-gap-3 tw-text-center"
                  >
                    {showLoadMore && (
                      <Button
                        onClick={() => {
                          setQueryState((prev) => ({
                            ...prev,
                            page: prev.page + 1,
                          }));
                        }}
                        variant="grey80"
                        modifier="outline"
                        label={loadMoreLabel}
                      />
                    )}
                    <Text variant="body-small">
                      {replaceValuesInLabel(resultsLabel, [
                        slicedListitems ? slicedListitems?.length : 0,
                        data?.total,
                      ])}
                    </Text>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </div>
      {/* Drawer filters */}
      {!!filters.length && (
        <DrawerWrapper
          isOpen={isMobileFiltersOpen}
          setIsOpen={setIsMobileFiltersOpen}
          slideFromLeft
          closeLabel={closeLabel}
          title={filterLabel}
          wrapperStyles="tw-h-full tw-flex tw-flex-col"
        >
          <form className="tw-flex tw-h-full tw-flex-col">
            <div className="tw-px-4 tw-py-10 md:tw-p-12">
              <FilterAccordion
                firstOpen
                allowMultipleOpen={false}
                variant="primary"
                setValue={setValue as any}
                register={register as any}
                filters={filters}
              />
            </div>
            <div className="tw-sticky tw-bottom-0 tw-left-0 tw-mt-auto tw-flex tw-w-full tw-bg-white">
              <Button
                variant="grey80"
                modifier="outline"
                buttonClasses="tw-basis-6/12 tw-rounded-none"
                label={clearAllLabel}
                onClick={(e) => {
                  clearAllFilters(e, {
                    freetext: queryState.freetext, // maintain search term
                    [listingFilterParam]: queryState?.[listingFilterParam], // maintain listing filters
                  });
                }}
                disabled={!mappedCheckedDrawerFilters.length}
              />
              <Button
                variant="grey80"
                buttonClasses="tw-basis-6/12 tw-rounded-none"
                onClick={(e) => {
                  e.preventDefault();
                  const submitHandler = handleSubmit(onSubmit);
                  submitHandler(e);
                  setIsMobileFiltersOpen(false);
                }}
                label={applyLabel}
              />
            </div>
          </form>
        </DrawerWrapper>
      )}
    </section>
  );
};

export default ListingWrapper;
